import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AuthClient from "../api/AuthClient";
import GroupsClient from "../api/GroupsClient";
import MyPartyDetail from "../components/MyPartyDetail/myPartyDetail";
import MyPartyDetailSkeleton from "../components/MyPartyDetail/MyPartyDetailSkeleton";
import NoPartyBanner from "../components/NoPartyBanner";
import { PartiesContext } from "../GlobalContext/partiesContext";
import SortPartyGroupsByDate from "../SortPartyGroupsByDate";

const MyPartyDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const {
    myPartiesInfo,
    selectedParty,
    setMyPartiesInfoContextData,
    setSelectedPartyContextData,
  } = useContext(PartiesContext);
  const [resolved, setResolved] = useState(false);

  const authClient = useMemo(() => new AuthClient(), []);
  const isLoggedIn = authClient.isLoggedIn();

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/login");
    }
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    if (!id) {
      setSelectedPartyContextData(null);
      setResolved(true);
      return;
    }

    if (selectedParty?.party.id === id) {
      setResolved(true);
      return;
    }

    const fromContext = myPartiesInfo.find((p) => p.party.id === id);
    if (fromContext) {
      setSelectedPartyContextData(fromContext);
      setResolved(true);
      return;
    }

    let cancelled = false;
    setResolved(false);
    const groupsClient = new GroupsClient(authClient);
    groupsClient.getParsedMyPartyById(id).then((data) => {
      if (cancelled) return;

      if (data?.data) {
        setSelectedPartyContextData(data.data);
        setMyPartiesInfoContextData((previousParties) => {
          const nextParties = previousParties.some((party) => party.party.id === data.data!.party.id)
            ? previousParties.map((party) =>
              party.party.id === data.data!.party.id ? data.data! : party
            )
            : [...previousParties, data.data!];

          return SortPartyGroupsByDate(nextParties);
        });
      } else {
        setSelectedPartyContextData(null);
      }

      setResolved(true);
    });

    return () => {
      cancelled = true;
    };
  }, [authClient, id, myPartiesInfo, selectedParty, setMyPartiesInfoContextData, setSelectedPartyContextData]);

  if (!resolved) {
    return <MyPartyDetailSkeleton />;
  }

  if (selectedParty?.party.id === id) {
    return <MyPartyDetail />;
  }

  return <NoPartyBanner />;
};

export default MyPartyDetails;
